import { useFields } from '@hooks'
import { useEffect, useMemo } from 'react'
import { Column, SortingRule, useFlexLayout, useSortBy, useTable } from 'react-table'
import { Card, Flexbox, Search, Table } from '@components/common'
import { DepreciationClass } from '@api/models'
import Pagination from '@components/Pagination'
import useDepreciationClassesTable from './useDepreciationClassesTable'
import { DEPRECIATION_CLASS_VIEWS } from '@constants'
import { useNavigate } from 'react-router-dom'
import DepreciationClassesTableLoading from './DepreciationClassesTableLoading'

const DepreciationClassesTable = ({ profile }) => {
  const {
    pageNumber,
    itemsPerPage,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
    isTotalCountEmpty,
    //
    searchKey,

    tableData,
    isLoadingTableData,
    isErrorGettingTableData,
    isRefetchingTableData,
  } = useDepreciationClassesTable({ profile })

  const navigate = useNavigate()

  // Declare columns
  const { fields, isLoading: isLoadingFields, isError: isErrorGettingFields } = useFields()

  const columns = useMemo<Column<DepreciationClass>[]>(
    () => [
      {
        Header: fields.DP_OBJ_ID,
        accessor: 'DP_OBJ_ID',
      },
      {
        Header: fields.DP_DESC,
        accessor: 'DP_DESC',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoadingFields]
  )

  // Declare react table

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: tableData ? tableData.list : [],
      manualSortBy: true,
      disableMultiSort: true,
      initialState: {
        sortBy: sortByInitialState() as SortingRule<DepreciationClass>[],
      },
    },
    useSortBy,
    useFlexLayout
  )

  // Update sort param
  useEffect(() => {
    updateSortSearchParam(sortBy, setSortBy)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy])

  // Returns
  const isTableLoading = isLoadingTableData || isLoadingFields
  const isError = isErrorGettingTableData || isErrorGettingFields

  if (isTableLoading) {
    return <DepreciationClassesTableLoading />
  }

  if (isError) {
    return (
      <Card spacing="none">
        <Table.NoResults heading="There was an error." />
      </Card>
    )
  }

  if (isTotalCountEmpty(tableData.count)) {
    return (
      <Card spacing="none">
        <Table.NoResults heading={`You haven't added any records yet.`} />
      </Card>
    )
  }

  return (
    <Card spacing="none">
      <Table.Actions>
        <Flexbox>
          <Search
            onChange={handleSearch}
            placeholder="Search by ID..."
            name={searchKey}
            width={'640px'}
            value={searchValue}
          />
        </Flexbox>
      </Table.Actions>
      {tableData.list.length === 0 ? (
        <Table.NoResults text="" />
      ) : (
        <>
          <Table
            {...getTableProps()}
            aria-label="Company Table"
            label="Company Table"
            aria-colcount={columns.length}
            aria-rowcount={tableData.list.length}
            height="calc(100vh - 280px)"
          >
            <Table.Head sticky>
              {isRefetchingTableData ? (
                <Table.LoadingRows />
              ) : (
                headerGroups.map((headerGroup, i) => (
                  <Table.HeadRow {...headerGroup.getHeaderGroupProps()} key={i}>
                    {headerGroup.headers.map((column, i) => (
                      <Table.Header
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        column={column}
                        key={i}
                        tabIndex={0}
                      >
                        {column.render('Header')}
                      </Table.Header>
                    ))}
                  </Table.HeadRow>
                ))
              )}
            </Table.Head>

            <Table.Body {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row)
                return (
                  <Table.Row
                    {...row.getRowProps()}
                    onClick={() => {
                      const routePath =
                        row.original.DP_TYP_CD === 'A' ? '/categories' : '/tax-classes'
                      navigate(
                        `${routePath}/${encodeURIComponent(row.original.DP_OBJ_ID)}/${
                          DEPRECIATION_CLASS_VIEWS.DETAILS
                        }`
                      )
                    }}
                    tabIndex={0}
                    key={i}
                  >
                    {row.cells.map(cell => (
                      <Table.Cell {...cell.getCellProps()} column={cell.column}>
                        {cell.render('Cell')}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table>
          <Pagination
            changePage={changePage}
            totalItems={tableData.count}
            maxItemsPerPage={itemsPerPage}
            currentPageNumber={pageNumber}
            label={`Showing ${tableData.list.length} of ${tableData.count} record${
              tableData.count > 1 ? 's' : ''
            }`}
          />
        </>
      )}
    </Card>
  )
}
export default DepreciationClassesTable
